import { Info } from 'lucide-react';
import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n';

/** Gap between the icon and the bubble, and the least room left at the viewport edge. */
const GAP = 8;

/** Long enough to cross from the icon onto the bubble without it closing. */
const CLOSE_DELAY = 120;

const HIDDEN: CSSProperties = { position: 'fixed', top: 0, left: 0, visibility: 'hidden' };

export interface FieldLabelProps {
  htmlFor: string;
  label: string;
  /** The field's explanation; given, an ⓘ follows the label and holds it. */
  info?: ReactNode;
}

/**
 * The label line of the field primitives. Without `info` it is exactly the
 * plain label they always drew; with it, the ⓘ sits beside - never inside -
 * the `<label>`, so the field's accessible name stays its label alone.
 */
export function FieldLabel({ htmlFor, label, info }: FieldLabelProps) {
  if (!info) {
    return (
      <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-ink">
        {label}
      </label>
    );
  }
  return (
    <div className="mb-1.5 flex items-center gap-1.5">
      <label htmlFor={htmlFor} className="text-sm font-medium text-ink">
        {label}
      </label>
      <InfoTip label={label}>{info}</InfoTip>
    </div>
  );
}

export interface InfoTipProps {
  /** The field this explains, for the icon's accessible name: "More about {label}". */
  label: string;
  /** The explanation itself. */
  children: ReactNode;
  className?: string;
}

/**
 * The ⓘ beside a field's label, and the explanation it holds.
 *
 * Nothing shows until it is asked for: hovering or focusing the icon opens the
 * bubble, and a click (or a tap, where there is no hover) pins it open until
 * Escape, a second click or a click elsewhere. The field primitives render one
 * from their `info` prop; reach for this directly only beside a label they do
 * not draw - a checkbox's, say. Keep it outside that `<label>`: inside, the
 * button's name would become part of the field's.
 *
 * Portalled and anchored like `Dropdown`, so a dialog or a scrolling inspector
 * cannot clip it, and stacked above `Modal`.
 */
export function InfoTip({ label, children, className }: InfoTipProps) {
  const t = useT();
  const tipId = useId();
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [style, setStyle] = useState<CSSProperties>(HIDDEN);
  const button = useRef<HTMLButtonElement>(null);
  const bubble = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | undefined>(undefined);
  const open = hovered || pinned;

  function show() {
    window.clearTimeout(closeTimer.current);
    setHovered(true);
  }
  function hideSoon() {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setHovered(false), CLOSE_DELAY);
  }
  function close() {
    window.clearTimeout(closeTimer.current);
    setHovered(false);
    setPinned(false);
  }

  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (button.current?.contains(target) || bubble.current?.contains(target)) return;
      close();
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      // Capture phase, and stopped: Escape here closes the tip, not the
      // dialog the field happens to sit in.
      event.stopPropagation();
      close();
    }

    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  // Layout effect so the bubble is placed before paint, never seen at 0,0.
  // Nothing to do while closed: a form can carry dozens of these, and every
  // one of them setting state as it mounts is work for nothing.
  useLayoutEffect(() => {
    if (!open) return;

    function place() {
      const anchor = button.current?.getBoundingClientRect();
      const size = bubble.current?.getBoundingClientRect();
      if (!anchor || !size) return;

      // Prefer below; flip above only when below overflows and above fits.
      const below = anchor.bottom + GAP;
      const above = anchor.top - GAP - size.height;
      const top = below + size.height > window.innerHeight - GAP && above > GAP ? above : below;

      const centred = anchor.left + anchor.width / 2 - size.width / 2;
      const rightMost = window.innerWidth - size.width - GAP;
      const left = Math.min(Math.max(GAP, centred), Math.max(GAP, rightMost));

      setStyle({ position: 'fixed', top, left, visibility: 'visible' });
    }

    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  return (
    <>
      <button
        ref={button}
        type="button"
        aria-label={t('ui.info_tip.about', { label })}
        aria-expanded={open}
        aria-describedby={open ? tipId : undefined}
        onClick={() => (pinned ? close() : setPinned(true))}
        onMouseEnter={show}
        onMouseLeave={hideSoon}
        onFocus={show}
        onBlur={() => {
          if (!pinned) hideSoon();
        }}
        className={cn(
          'inline-flex size-4 shrink-0 items-center justify-center rounded-full text-muted',
          'transition-colors hover:text-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-100',
          open && 'text-brand-600',
          className,
        )}
      >
        <Info className="size-3.5" aria-hidden="true" />
      </button>

      {open
        ? createPortal(
            <div
              ref={bubble}
              id={tipId}
              role="tooltip"
              style={style}
              onMouseEnter={show}
              onMouseLeave={hideSoon}
              className={cn(
                'z-[60] w-max max-w-[min(18rem,calc(100vw-1rem))] rounded-xl bg-ink px-3 py-2.5',
                'text-xs font-normal leading-relaxed whitespace-pre-line text-white shadow-lift',
              )}
            >
              {children}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
